// Counting on the handheld: the sheets to count at this location, then a sheet bin by bin in walking order. Go to the
// bin, count each line (blind unless the count says otherwise), add anything found that isn't on the sheet, and mark
// the bin done. Business Central is checked then for anything else in the bin, so nothing is missed. Stock can keep
// moving while you count: each line is compared with what BC had the moment you saved it. On a tag count, scan a line's
// tag to count it; anything found goes on a spare tag, scanned before it's added.
import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { wmsCounts, lotText, TAG_RE, type WmsSheet, type WmsSheetRow } from "../counts/countsApi";
import { wms } from "./wmsApi";
import { Alert, Header, I, LocChip, ScanField, Stepper, Steps, feedback, fmt, errText, errBody } from "./ui";

export function CountListPage({ loc }: { loc: string }) {
  const [d, setD] = useState<{ mine: WmsSheetRow[]; unassigned: WmsSheetRow[]; others: WmsSheetRow[] } | null>(null);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => { wmsCounts.sheets(loc).then(setD).catch((e) => setErr(errText(e))); }, [loc]);
  const card = (s: WmsSheetRow) => (
    <Link key={s.id} to={`/wms/count/${s.id}`} className={`w-pickcard ${s.status === "counting" && s.mine ? "started" : ""}`}>
      <div className="w-label-row"><span className="w-strong big">{s.name}</span><span className="w-muted w-mono">{s.number}</span></div>
      <span className="w-muted">{[s.countName, s.blind ? "Blind" : null, s.tagged ? "Tags" : null, s.round > 1 ? "Recount" : null].filter(Boolean).join(" · ")}</span>
      <div className="w-progress">
        <span className="w-bar"><span style={{ width: `${s.lines ? Math.round((s.counted / s.lines) * 100) : 0}%` }} /></span>
        <span className="w-small">{s.counted} of {s.lines} lines · {s.binsDone} of {s.bins} bins</span>
      </div>
      {s.assignedName && !s.mine && <span className="w-small">{s.assignedName} is counting it</span>}
    </Link>
  );
  return (
    <>
      <Header title="Count" sub="Sheets to count" back="/wms" right={<LocChip code={loc} />} />
      <div className="w-body">
        {err && <Alert title="Couldn't load the sheets">{err}</Alert>}
        {!d && !err && <p className="w-muted">Loading sheets…</p>}
        <Link to="/wms/quick" className="w-tile">
          <span className="w-tile-i"><I n="quick" s={28} /></span>
          <span className="w-tile-t"><span className="w-tile-n">Quick count</span><span className="w-muted">Scan a bin or an item and count it now</span></span>
          <I n="chev" />
        </Link>
        {d && (
          <>
            {d.mine.length > 0 && <div className="w-section"><div className="w-label">Yours</div><div className="w-list">{d.mine.map(card)}</div></div>}
            <div className="w-section"><div className="w-label">Open to anyone</div>
              {d.unassigned.length ? <div className="w-list">{d.unassigned.map(card)}</div> : <div className="w-card w-empty">No sheets waiting for someone at {loc}.</div>}</div>
            {d.others.length > 0 && <div className="w-section"><div className="w-label">Others are counting</div><div className="w-list">{d.others.map(card)}</div></div>}
          </>
        )}
      </div>
    </>
  );
}

type Line = WmsSheet["bins"][number]["lines"][number];

export function CountSheetPage() {
  const { id = "" } = useParams();
  const [d, setD] = useState<WmsSheet | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [bin, setBin] = useState<string | null>(null);
  const [atBin, setAtBin] = useState(false);
  const [line, setLine] = useState<Line | null>(null);
  const [qty, setQty] = useState(0);
  const [found, setFound] = useState<{ itemNo: string; lotNo: string; needLot: boolean; qty: number; tag: string } | null>(null);
  const [lineTag, setLineTag] = useState(""); // a spare scanned for a line that has no tag yet
  const [extra, setExtra] = useState<{ itemNo: string; lotNo: string; serialNo: string; description: string }[]>([]);
  const [finished, setFinished] = useState(false);
  const [overview, setOverview] = useState(false);

  const load = async (keepBin = true) => {
    const x = await wmsCounts.sheet(id);
    setD(x);
    if (!keepBin || !bin) { const next = x.bins.find((b) => !b.done); setBin(next?.bin ?? null); setAtBin(false); }
    return x;
  };
  useEffect(() => { load(false).catch((e) => setErr(errText(e))); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const cur = useMemo(() => d?.bins.find((b) => b.bin === bin) ?? null, [d, bin]);
  const run = async (fn: () => Promise<unknown>) => { setBusy(true); setErr(null); try { await fn(); } catch (e) { feedback.bad(); setErr(errText(e)); } finally { setBusy(false); } };

  if (!d) return (<><Header title="Count" back="/wms/count" /><div className="w-body">{err ? <Alert title="Couldn't open the sheet">{err}</Alert> : <p className="w-muted">Loading…</p>}</div></>);
  const { sheet, count, progress } = d;
  const sub = `${count.number} · ${progress.binsDone} of ${progress.bins} bins${count.blind ? " · blind" : ""}`;

  if (count.closed) return (<><Header title={sheet.name} sub={count.number} back="/wms/count" /><div className="w-body"><Alert tone="warn" title="This count is closed">It was posted or cancelled. There's nothing more to count on it.</Alert></div></>);

  // Someone has to take the sheet before counting it.
  if (!sheet.mine && sheet.status !== "done") return (
    <>
      <Header title={sheet.name} sub={sub} back="/wms/count" right={<LocChip code={count.location} />} />
      <div className="w-prompt">
        <span className="w-prompt-i"><I n="count" s={60} w={1.6} /></span>
        <h1>{sheet.assignedName ? `${sheet.assignedName} is counting this sheet` : "Count this sheet?"}</h1>
        <p>{progress.bins} bins, {progress.lines} lines{count.blind ? ". You won't see what Business Central expects." : "."}</p>
      </div>
      <div className="w-foot">
        {err && <Alert title="Couldn't start">{err}</Alert>}
        <button type="button" className="w-btn primary" disabled={busy} onClick={() => run(async () => { setD(await wmsCounts.start(id, !!sheet.assignedTo)); feedback.ok(); })}>
          {sheet.assignedTo ? "Take it over" : "Start counting"}
        </button>
      </div>
    </>
  );

  if (finished || sheet.status === "done") return (
    <>
      <Header title={sheet.name} sub={count.number} back="/wms/count" />
      <div className="w-body center">
        <span className="w-bigtick"><I n="check" s={60} w={2.6} /></span>
        <h1 className="w-h1">Sheet counted</h1>
        <p className="w-muted">{progress.lines} lines in {progress.bins} bins. Thanks — the lead reviews the differences.</p>
        <Link to="/wms/count" className="w-btn primary" style={{ alignSelf: "stretch" }}>Back to sheets</Link>
      </div>
    </>
  );

  const allBinsDone = d.bins.every((b) => b.done);
  const needsSpare = (l: Line) => count.tagged && !l.tag;
  const save = (l: Line, n: number | null) => run(async () => {
    if (needsSpare(l) && !lineTag) { feedback.bad(); setErr("Write it on a spare tag, then scan the tag."); return; }
    await wmsCounts.save(l.id, n, count.tagged ? l.tag ?? lineTag : undefined);
    feedback.ok(); setLine(null); await load();
  });
  const doneBin = () => run(async () => {
    if (!cur) return;
    const r = await wmsCounts.binDone(id, cur.bin);
    if (!r.done) { feedback.bad(); setExtra(r.added); await load(); return; }
    feedback.done(); setExtra([]);
    const x = await load(false);
    if (x.bins.every((b) => b.done)) setOverview(false);
  });
  const finish = () => run(async () => { await wmsCounts.done(id); feedback.done(); setFinished(true); });

  /** A scan in a bin: a lot or serial on the sheet, an item on it, or something found that isn't. */
  const scanInBin = (code: string) => run(async () => {
    if (!cur) return;
    const up = code.toUpperCase();
    if (count.tagged && TAG_RE.test(up)) {
      // A line's tag opens it, in whichever bin; a spare goes on what's open, or starts something found.
      const hit = d.bins.flatMap((b) => b.lines.map((l) => ({ bin: b.bin, l }))).find((x) => x.l.tag === up);
      if (hit) { feedback.ok(); if (hit.bin !== cur.bin) { setBin(hit.bin); setAtBin(true); setExtra([]); } open(hit.l); return; }
      if (d.spares.includes(up)) {
        feedback.ok(); setErr(null);
        if (line && !line.tag) { setLineTag(up); return; }
        if (found) { setFound({ ...found, tag: up }); return; }
        setLine(null); setFound({ itemNo: "", lotNo: "", needLot: false, qty: 0, tag: up }); return;
      }
      const t = await wmsCounts.tag(up);
      feedback.bad(); setErr(t.sheetId === id ? `${up} isn't a spare any more. Use another spare tag.` : `${up} is on ${t.sheet} of ${t.number}. You're counting ${sheet.name}.`); return;
    }
    const byLot = cur.lines.filter((l) => l.lotNo.toUpperCase() === up || l.serialNo.toUpperCase() === up);
    if (byLot.length === 1) { feedback.ok(); open(byLot[0]); return; }
    if (found?.needLot) { feedback.ok(); setFound({ ...found, lotNo: up, needLot: false }); return; }
    const byItem = cur.lines.filter((l) => l.itemNo.toUpperCase() === up);
    const r = byItem.length ? null : await wms.scan(count.location, code);
    const itemNo = byItem[0]?.itemNo ?? (r?.kind === "item" ? r.item.itemNo : null);
    if (!itemNo) { feedback.bad(); setErr(r?.kind === "bin" ? `That's bin ${r.bin.code}. You're counting ${cur.bin}.` : `${code} isn't an item or a lot on this sheet.`); return; }
    const ls = cur.lines.filter((l) => l.itemNo === itemNo);
    if (ls.length === 1 && !ls[0].lotNo && !ls[0].serialNo) { feedback.ok(); open(ls[0]); return; }
    if (ls.length) { feedback.ok(); setErr(`Scan the lot or serial number on the ${itemNo}, or tap its line.`); return; }
    feedback.ok(); setLine(null); setFound({ itemNo, lotNo: "", needLot: false, qty: 0, tag: found?.tag ?? "" });
  });
  const open = (l: Line) => { setLineTag(found?.tag && !l.tag ? found.tag : ""); setFound(null); setLine(l); setQty(l.counted ?? 0); setErr(null); };
  const saveFound = () => run(async () => {
    if (!found || !cur) return;
    try {
      await wmsCounts.found(id, { binCode: cur.bin, code: found.itemNo, lotNo: found.lotNo || undefined, counted: found.qty, tag: found.tag || undefined });
    } catch (e) {
      if (errBody(e).code === "tracking_required") { feedback.bad(); setFound({ ...found, needLot: true }); setErr(`${found.itemNo} is tracked by lot. Scan the lot number on it.`); return; }
      throw e;
    }
    feedback.ok(); setFound(null); await load();
  });

  // ----- all bins: pick one, or finish -----
  if (overview || !cur) return (
    <>
      <Header title={sheet.name} sub={sub} back={cur ? () => setOverview(false) : "/wms/count"} right={<LocChip code={count.location} />} />
      <Steps n={Math.max(1, progress.bins)} at={progress.binsDone} />
      <div className="w-body">
        {err && <Alert title="Not yet">{err}</Alert>}
        {allBinsDone && <Alert tone="ok" title="Every bin is done">Finish the sheet to hand it to the lead.</Alert>}
        <div className="w-list">
          {d.bins.map((b) => (
            <button key={b.bin} type="button" className={`w-row-btn ${b.bin === bin ? "sel" : ""}`} onClick={() => { setBin(b.bin); setAtBin(false); setOverview(false); setExtra([]); setLine(null); setFound(null); }}>
              <span className="w-row-main"><span className="w-mono w-strong">{b.bin || "No bin"}</span>
                <span className="w-muted">{b.lines.length ? `${b.lines.filter((l) => l.counted !== null).length} of ${b.lines.length} counted` : "Business Central shows it empty"}</span></span>
              {b.done ? <span className="w-tick"><I n="check" s={16} w={2.8} /></span> : <I n="chev" />}
            </button>
          ))}
        </div>
      </div>
      {allBinsDone && <div className="w-foot"><button type="button" className="w-btn primary" disabled={busy} onClick={finish}>Finish the sheet</button></div>}
    </>
  );

  // ----- one bin -----
  const counted = cur.lines.filter((l) => l.counted !== null).length;
  return (
    <>
      <Header title={sheet.name} sub={sub} back={() => setOverview(true)} right={<button type="button" className="w-smallbtn" onClick={() => setOverview(true)}>All bins</button>} />
      <Steps n={Math.max(1, progress.bins)} at={progress.binsDone} />
      {!atBin && !cur.done ? (
        <>
          <div className="w-body">
            <div className="w-go-card"><span className="w-go-l">Go to</span><span className="w-go-c">{cur.bin || "No bin"}</span>
              <span className="w-go-s">{cur.lines.length ? `${cur.lines.length} line${cur.lines.length === 1 ? "" : "s"} to count` : "Business Central shows it empty"}</span></div>
            {err && <Alert title="Wrong bin">{err}</Alert>}
          </div>
          <div className="w-foot">
            <ScanField label="Scan the bin when you're there" onScan={(c) => { if (c.toUpperCase() === cur.bin) { feedback.ok(); setAtBin(true); setErr(null); } else { feedback.bad(); setErr(`You scanned ${c.toUpperCase()}. Go to ${cur.bin}.`); } }} />
            <button type="button" className="w-link" onClick={() => setAtBin(true)}>I'm at {cur.bin || "it"}</button>
          </div>
        </>
      ) : (
        <>
          <div className="w-body">
            <div className="w-card w-confirm">
              <span className="w-tick"><I n="check" s={18} w={2.5} /></span>
              <span className="w-row-main"><span className="w-small">Counting bin{count.tagged ? ` · ${d.spares.length} spare tag${d.spares.length === 1 ? "" : "s"} left` : ""}</span><span className="w-mono w-strong big">{cur.bin || "No bin"}</span></span>
              <span className="w-muted">{counted} of {cur.lines.length}</span>
            </div>
            {err && <Alert title="Not that one">{err}</Alert>}
            {extra.length > 0 && <Alert tone="warn" title={`Business Central also shows ${extra.length === 1 ? "this" : "these"} in ${cur.bin}`}>
              {extra.map((x) => [x.itemNo, lotText(x)].filter(Boolean).join(" ")).join(", ")}. Count {extra.length === 1 ? "it" : "them"}, or enter 0 if {extra.length === 1 ? "it isn't" : "they aren't"} there.</Alert>}
            {!cur.lines.length && !found && <div className="w-card w-empty">Business Central shows {cur.bin} empty. Scan anything you find, or mark it empty.</div>}
            <div className="w-list">
              {cur.lines.map((l) => (
                <button key={l.id} type="button" className={`w-row-btn ${line?.id === l.id ? "sel" : ""}`} onClick={() => open(l)}>
                  <span className="w-row-main"><span className="w-strong">{l.itemNo}</span>
                    <span className="w-muted">{[l.tag, l.description, lotText(l), l.added && "Found here"].filter(Boolean).join(" · ")}</span>
                    {l.moved && <span className="w-repl-warn">Stock moved here during the count</span>}</span>
                  {l.counted !== null ? <span className="w-strong">{fmt(l.counted)} {l.uom ?? ""}</span> : <span className="w-tag">Count</span>}
                </button>
              ))}
            </div>
            {line && (
              <div className="w-card w-qtycard">
                <label htmlFor="count-qty" className="w-label">How many {line.itemNo}{line.lotNo ? `, lot ${line.lotNo}` : line.serialNo ? `, SN ${line.serialNo}` : ""}?</label>
                <Stepper id="count-qty" value={qty} onChange={setQty} max={line.serialNo ? 1 : undefined} of={line.uom ?? undefined} big />
                {line.expected !== null && <span className="w-muted">Business Central expected {fmt(line.expected)}</span>}
                {count.tagged && (line.tag ? <span className="w-muted">On <span className="w-mono">{line.tag}</span>: write the count on the tag too.</span>
                  : lineTag ? <span className="w-muted">On spare <span className="w-mono w-strong">{lineTag}</span></span>
                  : <span className="w-repl-warn">It has no tag. Write it on a spare tag and scan the tag.</span>)}
                <div className="w-two">
                  <button type="button" className="w-btn" disabled={busy || (needsSpare(line) && !lineTag)} onClick={() => save(line, 0)}>Not here</button>
                  <button type="button" className="w-btn primary" disabled={busy || (needsSpare(line) && !lineTag)} onClick={() => save(line, qty)}>Save</button>
                </div>
              </div>
            )}
            {found && (
              <div className="w-card w-qtycard">
                <span className="w-label">{found.itemNo ? <>Found {found.itemNo}{found.lotNo ? `, lot ${found.lotNo}` : ""} in {cur.bin}. It isn't on the sheet.</> : <>Spare tag {found.tag}</>}</span>
                {count.tagged && found.itemNo && (found.tag ? <span className="w-muted">On spare <span className="w-mono w-strong">{found.tag}</span></span> : <span className="w-repl-warn">Write it on a spare tag and scan the tag.</span>)}
                {!found.itemNo ? <>
                  <span className="w-muted">Scan the item you found, and write it on the tag.</span>
                  <button type="button" className="w-btn" onClick={() => { setFound(null); setErr(null); }}>Cancel</button>
                </> : found.needLot ? <span className="w-muted">Scan its lot number below.</span> : <>
                  <Stepper id="found-qty" value={found.qty} onChange={(n) => setFound({ ...found, qty: n })} big />
                  <div className="w-two">
                    <button type="button" className="w-btn" onClick={() => { setFound(null); setErr(null); }}>Cancel</button>
                    <button type="button" className="w-btn primary" disabled={busy || !(found.qty > 0) || (count.tagged && !found.tag)} onClick={saveFound}>Add it</button>
                  </div>
                </>}
              </div>
            )}
          </div>
          <div className="w-foot">
            <ScanField label={found?.needLot ? "Scan the lot number" : found && !found.itemNo ? "Scan the item you found" : count.tagged && ((line && !line.tag && !lineTag) || (found && !found.tag)) ? "Scan the spare tag" : count.tagged ? "Scan a tag, item or lot" : "Scan an item or lot"} onScan={scanInBin} busy={busy} />
            <button type="button" className="w-btn primary" disabled={busy || counted < cur.lines.length || !!line || (!!found && !found.needLot)} onClick={doneBin}>
              {cur.lines.length ? (counted < cur.lines.length ? `Count ${cur.lines.length - counted} more` : `${cur.bin} is done`) : `${cur.bin || "Bin"} is empty`}
            </button>
          </div>
        </>
      )}
    </>
  );
}
